import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronIcon, CopyIcon, DateTimePicker, EditIcon, ErrorState, LoadingState, SearchIcon, toISODate, useToast } from "@signet/ui";
import { CreatorAvatar, useCreatorPhotos, useMe } from "@signet/web-kit";
import { BANDS, formatMessage } from "../shared/flow.ts";
import { BASE, dayLabel, fetchDay, fetchRange, shiftDay, subsKeys } from "./api.ts";
import { BandTabs, CountsOnly, FlowTable, PlatformSwitch, rowsFor, Summary, useBand, usePlatform } from "./board.tsx";
import { UrgentView } from "./urgent.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Sub flow: one day's subs per creator, in the boss's four bands. The day
   lives in the address (?day=); without one it is the latest report, which
   is yesterday in Melbourne: the afternoon message reports the day before.
   ────────────────────────────────────────────────────────────────────────── */

/** The three pages of the module, each shown to whoever may open it. */
export function SubsTabs({ on }: { on: "flow" | "months" | "enter" }) {
  const { can } = useMe();
  const tabs = [
    { id: "flow", label: "Sub Flow", to: "/subs", needs: "subs.flow:view" },
    { id: "months", label: "Months", to: "/subs/months", needs: "subs.flow:view" },
    { id: "enter", label: "Enter subs", to: "/subs/enter", needs: "subs.entry:view" },
  ].filter((t) => can(t.needs));
  if (tabs.length < 2) return null;
  return (
    <nav className="subs-tabs" aria-label="Sub Flow">
      {tabs.map((t) => (
        <Link key={t.id} to={t.to as never} className={on === t.id ? "on" : ""}>
          {t.label}
        </Link>
      ))}
    </nav>
  );
}

const minDay = (a: string, b: string) => (a < b ? a : b);

/**
 * "Mon, Oct 12 (yesterday)" for the newest day there can be numbers for, which is always yesterday in
 * Melbourne. It said "(today's report)" until Jay read that as the wrong day (2026-10-13): the label
 * names the day the numbers are for, not the day they were sent.
 */
export const dayName = (day: string, latest: string) =>
  day === latest ? `${dayLabel(day)} (yesterday)` : day === shiftDay(latest, 1) ? `${dayLabel(day)} (today)` : dayLabel(day, day.slice(0, 4) !== latest.slice(0, 4));

/** The chip for a day nothing was entered for (Jay, 2026-10-13: "No data for today"). */
export const noDataFor = (day: string, latest: string) => (day === shiftDay(latest, 1) ? "No data for today" : day === latest ? "No data for yesterday" : "No data for this day");

/**
 * Back as far as anyone likes, forward to today (Jay, 2026-10-13: "i dont have the option to input
 * today's?"). It opens on yesterday, the day the afternoon message reports; today is there for numbers
 * kept for the day itself.
 */
export function DayStepper({ day, latest, onDay }: { day: string; latest: string; onDay: (day: string) => void }) {
  const today = shiftDay(latest, 1);
  return (
    <div className="subs-day">
      <button type="button" className="icon-button" aria-label="Day before" onClick={() => onDay(shiftDay(day, -1))}>
        <ChevronIcon size={15} direction="left" />
      </button>
      <DateTimePicker
        label="Pick a day"
        className="subs-day-pick"
        withTime={false}
        clearable={false}
        compact
        value={new Date(`${day}T12:00:00`).toISOString()}
        renderValue={() => dayName(day, latest)}
        // Nothing after today can have numbers.
        onChange={(iso) => iso && onDay(minDay(toISODate(new Date(iso)), today))}
      />
      <button type="button" className="icon-button" aria-label="Day after" disabled={day >= today} onClick={() => onDay(shiftDay(day, 1))}>
        <ChevronIcon size={15} direction="right" />
      </button>
      {day !== latest ? (
        <button type="button" className="text-button tiny accent" onClick={() => onDay(latest)}>
          Yesterday
        </button>
      ) : null}
    </div>
  );
}

export function FlowPage() {
  const { can } = useMe();
  // Someone who only types the subs in has nothing to see here.
  if (!can("subs.flow:view")) return <Navigate to={"/subs/enter" as never} replace />;
  return <Flow />;
}

function Flow() {
  const search = useSearch({ strict: false } as never) as { day?: string; from?: string; to?: string };
  const navigate = useNavigate();
  const { can } = useMe();
  const toast = useToast();
  // Several days (?from=&to=), as a Now card opens it for 7D, 30D or picked dates (Jay, 2026-10-13):
  // totals over the days, each creator banded by their average day.
  const span = search.from && search.to && search.from <= search.to ? { key: "custom" as const, from: search.from, to: search.to } : null;
  const flow = useQuery({
    queryKey: span ? subsKeys.range(span) : subsKeys.day(search.day ?? "latest"),
    queryFn: () => (span ? fetchRange(span) : fetchDay(search.day)),
    refetchInterval: 60_000,
  });
  const photos = useCreatorPhotos(BASE);
  const [platform, setPlatform] = usePlatform();
  const [band, setBand] = useBand(flow.data, platform);
  const [find, setFind] = useState("");

  const data = flow.data;
  const latest = data?.latest ?? search.day ?? "";
  const day = search.day ?? latest;
  const setDay = (next: string) => navigate({ to: ".", search: (next === latest ? {} : { day: next }) as never, replace: true });
  const words = find.trim().toUpperCase();
  const rows = (data && band !== "urgent" ? rowsFor(data, band, platform) : []).filter((r) => !words || r.creator.includes(words));
  const current = band === "list" ? { label: "Fansly", range: "every page" } : BANDS.find((b) => b.id === band) ?? BANDS[0]!;

  const copy = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(formatMessage(data.rows));
      toast.success("Copied", "The day's sub flow is on the clipboard in the message format.");
    } catch {
      toast.error("Couldn't copy", "The browser didn't allow it. Try again.");
    }
  };

  return (
    <div className="page subs-page">
      <header className="page-head">
        <div>
          <h1>Sub Flow</h1>
          <p>New subs per creator and page, typed in each afternoon. Click a band to see who is in it.</p>
        </div>
        <div className="subs-head-actions">
          {data && !data.noData && !span ? (
            <button type="button" className="secondary-button" onClick={copy}>
              <CopyIcon size={14} /> Copy as message
            </button>
          ) : null}
          {can("subs.entry:view") ? (
            <Link to={"/subs/enter" as never} search={(!span && day && day !== latest ? { day } : {}) as never} className="button brass">
              <EditIcon size={14} /> Enter / edit subs
            </Link>
          ) : null}
        </div>
      </header>
      <SubsTabs on="flow" />

      {flow.isLoading ? (
        <LoadingState />
      ) : flow.isError || !data ? (
        <ErrorState title="Couldn't load Sub Flow" onRetry={() => flow.refetch()} />
      ) : (
        <>
          <div className="subs-bar">
            {span ? (
              <div className="subs-day">
                <span className="subs-day-pick">
                  {dayLabel(span.from)} to {dayLabel(span.to)}
                </span>
                <button type="button" className="text-button tiny accent" onClick={() => setDay(latest)}>
                  One day
                </button>
              </div>
            ) : (
              <DayStepper day={day} latest={latest} onDay={setDay} />
            )}
            {data.noData ? <span className="signet-stat-chip">{span ? "No data for these days" : noDataFor(day, latest)}</span> : null}
            <Summary flow={data} versus={span ? "vs prior period" : "vs the day before"} />
          </div>

          {/* Above the bands, the full width of them (Jay, 2026-10-13). */}
          <PlatformSwitch value={platform} onChange={setPlatform} />
          <BandTabs flow={data} band={band} onBand={setBand} platform={platform} />

          {band === "urgent" ? (
            <UrgentView day={span ? span.to : day} photos={photos} />
          ) : (
            <section className={`panel subs-section subs-section-${band}`}>
              <div className="subs-section-head">
                <div>
                  <h2>
                    {current.label} <span className="subs-faint">{band === "list" ? current.range : `${current.range} subs${span ? " a day, on average" : ""}`}</span>
                  </h2>
                  <p>
                    {(() => {
                      const n = rows.length + (band === "list" && !words ? (data.countsOnly ?? []).length : 0);
                      return `${n} ${n === 1 ? "creator" : "creators"}`;
                    })()}
                    {words ? " matching" : band === "list" ? "" : " in this band"}
                  </p>
                </div>
                <div className="subs-filters">
                  <label className="subs-search">
                    <SearchIcon size={14} />
                    <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Search creators" aria-label="Search creators" />
                  </label>
                </div>
              </div>
              {band === "list" && !rows.length && data.countsOnly?.length ? null : <FlowTable flow={data} rows={rows} photos={photos} oneDay={!span} />}
                {band === "list" ? <CountsOnly flow={data} photos={photos} /> : null}
            </section>
          )}

          {!span && !data.noData && data.missing.length ? (
            <p className="subs-missing">
              <b>Not in yet ({data.missing.length}):</b>
              {/* Every name with its photo (Jay, 2026-10-13). "JACKIE FANSLY" is JACKIE's photo. */}
              {data.missing.map((n) => (
                <span key={n} className="subs-missing-who">
                  <CreatorAvatar name={n.replace(/ FANSLY$/, "")} src={photos(n.replace(/ FANSLY$/, ""))} size={18} />
                  {n}
                </span>
              ))}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
